/**
 * LandingTerminal - Portada pública estilo terminal financiero (Investing / DEGIRO)
 * Datos en vivo (yfinance vía /api/stock/batch, público). Estética oscura/neón densa KAPITAL.
 * 100% en ESPAÑOL y responsive (móvil incluido).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Search, LogIn, ArrowUpRight, Menu, X } from 'lucide-react';
import Sparkline from './Sparkline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Paleta de MARCA KAPITAL (extraída del logo: dorado metálico + plata)
const GOLD = 'linear-gradient(135deg,#FBE9A0 0%,#E8C55C 38%,#C9A227 68%,#A67C00 100%)';
const SILVER = 'linear-gradient(135deg,#F3F5F8 0%,#C8CED6 45%,#98A0AC 100%)';

// Logos de brokers compatibles (wordmarks monocromo plateado; hover → dorado)
const BROKERS = [
  { name: 'Trade Republic', cls: 'font-black uppercase tracking-tight' },
  { name: 'IBKR', cls: 'font-black uppercase tracking-tight' },
  { name: 'DEGIRO', cls: 'font-black uppercase tracking-[0.14em]' },
  { name: 'XTB', cls: 'font-black uppercase tracking-[0.28em]' },
  { name: 'Trading 212', cls: 'font-black uppercase tracking-tight' },
  { name: 'eToro', cls: 'font-black uppercase tracking-tight' },
];
const BrokerLogo = ({ name, cls }) => (
  <div className="flex items-center h-12 md:h-14 px-5 rounded-xl border border-gold-400/30 bg-slate-800/50 hover:border-gold-400/70 hover:bg-slate-800/80 transition-all group shadow-[0_2px_12px_rgba(0,0,0,0.35)]" data-testid={`broker-${name.replace(/\s+/g, '-').toLowerCase()}`}>
    <span className={`text-base md:text-lg ${cls} text-transparent bg-clip-text whitespace-nowrap group-hover:brightness-110`} style={{ backgroundImage: SILVER }}>{name}</span>
  </div>
);

// Activos destacados para la rejilla "Mercados de un vistazo"
const OVERVIEW = [
  { t: '^GSPC', label: 'S&P 500', cat: 'Índice' },
  { t: '^IXIC', label: 'Nasdaq 100', cat: 'Índice' },
  { t: '^IBEX', label: 'IBEX 35', cat: 'Índice' },
  { t: '^STOXX50E', label: 'EuroStoxx 50', cat: 'Índice' },
  { t: 'GC=F', label: 'Oro', cat: 'Materia prima' },
  { t: 'BZ=F', label: 'Petróleo Brent', cat: 'Materia prima' },
  { t: 'BTC-USD', label: 'Bitcoin', cat: 'Cripto' },
  { t: 'EURUSD=X', label: 'EUR / USD', cat: 'Divisa' },
  { t: 'EURMXN=X', label: 'EUR / MXN', cat: 'Divisa' },
  { t: '^MXX', label: 'IPC México', cat: 'Índice' },
];

const TAPE_EXTRA = ['^DJI', '^GDAXI', '^N225', 'ETH-USD', 'SOL-USD', 'SI=F', 'CL=F', 'GBPUSD=X', 'USDJPY=X', '^MXX', 'EURMXN=X'];

// Universo amplio y multi-región para que "Subidas/Bajadas" sea representativo a cualquier hora
// (EE.UU. en horario americano, Europa/UK en horario europeo, México en horario BMV).
const STOCK_UNIVERSE = [
  // EE.UU.
  'AAPL', 'MSFT', 'NVDA', 'TSLA', 'AMZN', 'GOOGL', 'META', 'AMD', 'NFLX', 'JPM',
  'KO', 'DIS', 'INTC', 'PYPL', 'UBER', 'BAC', 'XOM', 'WMT', 'V', 'MA',
  'PFE', 'BA', 'NKE', 'SBUX', 'COIN',
  // Europa continental
  'ASML.AS', 'SAP.DE', 'MC.PA', 'OR.PA', 'SIE.DE', 'AIR.PA', 'TTE.PA',
  'SAN.MC', 'BBVA.MC', 'ITX.MC', 'IBE.MC', 'ENEL.MI', 'NESN.SW', 'NOVN.SW',
  // Reino Unido
  'SHEL.L', 'AZN.L', 'HSBA.L',
  // México (BMV)
  'AMXB.MX', 'WALMEX.MX', 'GFNORTEO.MX', 'FEMSAUBD.MX', 'GMEXICOB.MX', 'BIMBOA.MX', 'CEMEXCPO.MX', 'KIMBERA.MX',
];

// Nombres cortos en español para las acciones europeas y mexicanas (yfinance devuelve nombres largos)
const STOCK_LABELS = {
  'ASML.AS': 'ASML', 'SAP.DE': 'SAP', 'MC.PA': 'LVMH', 'OR.PA': "L'Oréal",
  'SIE.DE': 'Siemens', 'AIR.PA': 'Airbus', 'TTE.PA': 'TotalEnergies',
  'SAN.MC': 'Santander', 'BBVA.MC': 'BBVA', 'ITX.MC': 'Inditex', 'IBE.MC': 'Iberdrola',
  'ENEL.MI': 'Enel', 'NESN.SW': 'Nestlé', 'NOVN.SW': 'Novartis',
  'SHEL.L': 'Shell', 'AZN.L': 'AstraZeneca', 'HSBA.L': 'HSBC',
  'AMXB.MX': 'América Móvil', 'WALMEX.MX': 'Walmart de México', 'GFNORTEO.MX': 'Banorte',
  'FEMSAUBD.MX': 'FEMSA', 'GMEXICOB.MX': 'Grupo México', 'BIMBOA.MX': 'Bimbo',
  'CEMEXCPO.MX': 'Cemex', 'KIMBERA.MX': 'Kimberly-Clark México',
};

const TAPE_LABELS = {
  '^GSPC': 'S&P 500', '^IXIC': 'Nasdaq', '^IBEX': 'IBEX 35', '^STOXX50E': 'EuroStoxx',
  '^DJI': 'Dow Jones', '^GDAXI': 'DAX', '^N225': 'Nikkei', 'GC=F': 'Oro', 'SI=F': 'Plata',
  'BZ=F': 'Brent', 'CL=F': 'WTI', 'BTC-USD': 'Bitcoin', 'ETH-USD': 'Ethereum', 'SOL-USD': 'Solana',
  'EURUSD=X': 'EUR/USD', 'GBPUSD=X': 'GBP/USD', 'USDJPY=X': 'USD/JPY', '^MXX': 'IPC México', 'EURMXN=X': 'EUR/MXN',
};

const ALL_TICKERS = Array.from(new Set([...OVERVIEW.map((o) => o.t), ...TAPE_EXTRA, ...STOCK_UNIVERSE]));
const TAPE_TICKERS = Array.from(new Set([...OVERVIEW.map((o) => o.t), ...TAPE_EXTRA]));

const fmtPrice = (v) => {
  if (v == null || isNaN(v)) return '—';
  const abs = Math.abs(v);
  const dec = abs >= 1000 ? 0 : abs >= 1 ? 2 : 4;
  return v.toLocaleString('es-ES', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};
const fmtPct = (p) => (p == null || isNaN(p) ? '—' : `${p >= 0 ? '+' : ''}${p.toFixed(2)}%`);

const NAV_LINKS = [
  { label: 'Mercados', target: 'mercados' },
  { label: 'Funciones', target: 'features' },
  { label: 'Planes', target: 'planes' },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// ---------------- Ticker Tape ----------------
const TickerTape = ({ quotes }) => {
  const items = TAPE_TICKERS.map((t) => quotes[t]).filter(Boolean);
  if (items.length === 0) return <div className="h-9 w-full bg-[#0b1220] border-b border-white/5" data-testid="ticker-tape" />;
  const row = [...items, ...items];
  return (
    <div className="w-full overflow-hidden bg-[#0b1220] border-b border-white/5 py-2 group" data-testid="ticker-tape">
      <div className="flex whitespace-nowrap animate-kap-tape group-hover:[animation-play-state:paused]">
        {row.map((q, i) => {
          const up = (q.day_change_percent ?? 0) >= 0;
          return (
            <div key={i} className="flex items-center gap-2 px-5 border-r border-white/5 font-mono text-xs">
              <span className="text-slate-300 font-semibold">{TAPE_LABELS[q.ticker] || q.name || q.ticker}</span>
              <span className="text-white">{fmtPrice(q.current_price)}</span>
              <span className={`flex items-center gap-0.5 font-semibold ${up ? 'text-[#00ff88]' : 'text-[#ff3b30]'}`}>
                {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                {fmtPct(q.day_change_percent)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------- Overview Card ----------------
const MarketCard = ({ q, meta, spark, onOpen }) => {
  if (!q) {
    return <div className="h-[92px] rounded-lg bg-[#111827]/60 border border-white/5 animate-pulse" />;
  }
  const up = (q.day_change_percent ?? 0) >= 0;
  return (
    <div
      onClick={() => onOpen && onOpen(meta.t)}
      className="group cursor-pointer bg-[#111827]/80 border border-white/5 rounded-lg p-3 flex flex-col justify-between hover:border-gold-400/50 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-gold-500/10 transition-all duration-200"
      data-testid={`market-card-${meta.t}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[13px] font-semibold text-white leading-tight">{meta.label}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{meta.cat}</div>
        </div>
        <div className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${up ? 'text-[#00ff88] bg-[#00ff88]/10' : 'text-[#ff3b30] bg-[#ff3b30]/10'}`}>
          {fmtPct(q.day_change_percent)}
        </div>
      </div>
      <div className="flex items-end justify-between gap-2 mt-2">
        <div className="font-mono text-lg font-bold text-white tabular-nums">{fmtPrice(q.current_price)}</div>
        <div className="h-8 w-20 shrink-0">
          {spark && spark.length > 1 ? (
            <Sparkline data={spark} dayChangePercent={q.day_change_percent} strokeWidth={2} />
          ) : (
            <div className="w-full h-full rounded bg-white/5" />
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------- Movers Table ----------------
const MoversTable = ({ title, rows, icon: Icon, accent, testid, onPick, loading }) => (
  <div className="bg-[#111827]/70 border border-white/5 rounded-xl overflow-hidden" data-testid={testid}>
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5 bg-[#0d1326]">
      {Icon && <Icon size={15} className={accent} />}
      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-200">{title}</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm font-mono">
        <thead>
          <tr className="text-slate-400 font-sans text-[11px] uppercase tracking-wider">
            <th className="text-left font-medium px-4 py-2">Activo</th>
            <th className="text-right font-medium px-4 py-2">Precio</th>
            <th className="text-right font-medium px-4 py-2">Var %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((q) => {
            const up = (q.day_change_percent ?? 0) >= 0;
            return (
              <tr key={q.ticker} onClick={() => onPick && onPick(q.ticker)} className="border-t border-white/5 hover:bg-gold-500/10 cursor-pointer transition-colors duration-150">
                <td className="px-4 py-2 text-left">
                  <span className="text-white font-semibold">{q.ticker.replace('-USD', '').replace('=F', '').replace('=X', '')}</span>
                  <span className="hidden sm:inline text-slate-500 font-sans text-xs ml-2">{(q.name || '').slice(0, 18)}</span>
                </td>
                <td className="px-4 py-2 text-right text-slate-200 tabular-nums">{fmtPrice(q.current_price)}</td>
                <td className={`px-4 py-2 text-right font-semibold tabular-nums ${up ? 'text-[#00ff88]' : 'text-[#ff3b30]'}`}>
                  {fmtPct(q.day_change_percent)}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`} className="border-t border-white/5">
                  <td className="px-4 py-2.5"><div className="h-3.5 w-24 rounded animate-pulse" style={{ background: 'linear-gradient(90deg, rgba(212,175,55,0.10), rgba(232,197,92,0.24), rgba(212,175,55,0.10))' }} /></td>
                  <td className="px-4 py-2.5 text-right"><div className="h-3.5 w-14 ml-auto rounded bg-white/5 animate-pulse" /></td>
                  <td className="px-4 py-2.5 text-right"><div className="h-3.5 w-12 ml-auto rounded bg-white/5 animate-pulse" /></td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-500 font-sans text-xs">Sin datos disponibles ahora mismo</td></tr>
            )
          )}
        </tbody>
      </table>
    </div>
  </div>
);

// ---------------- Buscador de activos (en vivo, sobre el universo cargado) ----------------
const AssetSearch = ({ quotes, onPick }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const index = useMemo(() => Object.values(quotes).map((q) => ({
    ticker: q.ticker,
    name: STOCK_LABELS[q.ticker] || TAPE_LABELS[q.ticker] || q.name || q.ticker,
    price: q.current_price,
    pct: q.day_change_percent,
  })), [quotes]);

  const results = useMemo(() => {
    const s = query.trim().toLowerCase();
    if (!s) return [];
    return index
      .filter((r) => r.ticker.toLowerCase().includes(s) || (r.name || '').toLowerCase().includes(s))
      .slice(0, 8);
  }, [query, index]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (ticker) => { setOpen(false); setQuery(''); onPick(ticker); };
  const q = query.trim();

  return (
    <div className="relative w-full" ref={boxRef} data-testid="asset-search">
      <div className="flex items-center gap-2 px-3 h-11 rounded-lg bg-[#111827] border border-white/10 focus-within:border-gold-500/50 transition-colors duration-200">
        <Search size={16} className="text-gold-400 shrink-0" />
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === 'Enter' && q) pick((results[0]?.ticker) || q.toUpperCase()); }}
          placeholder="Buscar un activo (AAPL, BTC, IBEX…)"
          className="w-full bg-transparent outline-none text-sm text-white placeholder:text-slate-500"
          data-testid="asset-search-input"
        />
      </div>
      {open && q && (
        <div className="absolute z-40 mt-2 w-full max-h-80 overflow-y-auto rounded-lg bg-[#0d1326] border border-white/10 shadow-2xl" data-testid="asset-search-results">
          {results.length === 0 && Object.keys(quotes).length === 0 && (
            <div className="px-3 py-2.5 text-xs text-slate-400 font-sans border-b border-white/5">Cargando datos de mercado…</div>
          )}
          {results.map((r) => {
            const up = (r.pct ?? 0) >= 0;
            return (
              <button
                key={r.ticker}
                onClick={() => pick(r.ticker)}
                className="w-full flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-white/5 transition-colors duration-150 border-b border-white/5 text-left"
                data-testid={`asset-search-item-${r.ticker}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white font-mono">{r.ticker.replace('-USD', '').replace('=F', '').replace('=X', '')}</div>
                  <div className="text-xs text-slate-400 truncate">{r.name}</div>
                </div>
                <div className="text-right font-mono shrink-0">
                  <div className="text-sm text-slate-200">{fmtPrice(r.price)}</div>
                  <div className={`text-xs font-semibold ${up ? 'text-[#00ff88]' : 'text-[#ff3b30]'}`}>{fmtPct(r.pct)}</div>
                </div>
              </button>
            );
          })}
          <button
            onClick={() => pick(q.toUpperCase())}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-gold-400 hover:bg-gold-500/10 transition-colors duration-150 text-sm font-medium"
            data-testid="asset-search-analyze"
          >
            <ArrowUpRight size={15} /> Ver análisis de “{q.toUpperCase()}”
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------- Top Nav ----------------
const TopNav = ({ onGetStarted }) => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 w-full backdrop-blur-xl bg-[#0a0e1a]/90 border-b border-white/10" data-testid="landing-topnav">
      <div className="max-w-[1600px] mx-auto h-24 md:h-28 px-4 md:px-8 flex items-center justify-between">
        <button onClick={() => scrollTo('mercados')} className="flex items-center gap-3 md:gap-4 shrink-0 group" data-testid="nav-logo">
          <img src="/logo-kapital-at.png" alt="Análisis Técnico Kapital" className="h-16 md:h-20 w-auto object-contain transition-transform duration-200 group-hover:scale-105" style={{ mixBlendMode: 'lighten' }} />
          <span className="kap-link-gold hidden md:inline text-lg lg:text-xl font-bold tracking-wide text-transparent bg-clip-text font-mono transition-all duration-200" style={{ backgroundImage: GOLD }}>analisistecnicokapital.com</span>
        </button>
        <nav className="hidden md:flex items-center gap-2">
          {NAV_LINKS.map((l) => (
            <button
              key={l.target}
              onClick={() => scrollTo(l.target)}
              className="kap-link px-4 py-2.5 text-base font-semibold text-slate-300 hover:text-gold-300 transition-colors duration-200"
              data-testid={`nav-link-${l.target}`}
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2 md:gap-4">
          <button
            onClick={onGetStarted}
            className="kap-link hidden sm:flex items-center gap-1.5 px-4 py-2.5 text-base font-bold text-slate-100 hover:text-gold-300 transition-colors duration-200"
            data-testid="nav-login-btn"
          >
            <LogIn size={19} /> Iniciar sesión
          </button>
          <button
            onClick={onGetStarted}
            className="flex items-center gap-1.5 h-12 md:h-14 px-6 md:px-8 rounded-lg font-extrabold text-base md:text-lg text-slate-900 transition-transform duration-200 hover:scale-105"
            style={{ background: GOLD, boxShadow: '0 0 24px rgba(212,175,55,0.40)' }}
            data-testid="nav-cta-btn"
          >
            Empezar gratis
          </button>
          <button className="md:hidden p-2 text-slate-300" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Menú">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#0a0e1a]/95 px-4 py-3 flex flex-col gap-1" data-testid="nav-mobile-menu">
          {NAV_LINKS.map((l) => (
            <button key={l.target} onClick={() => { scrollTo(l.target); setOpen(false); }} className="kap-link text-left px-2 py-2 text-sm text-slate-300 hover:text-gold-300">
              {l.label}
            </button>
          ))}
          <button onClick={onGetStarted} className="kap-link text-left px-2 py-2 text-sm text-gold-400 font-semibold flex items-center gap-1.5">
            <LogIn size={15} /> Iniciar sesión
          </button>
        </div>
      )}
    </header>
  );
};

// ---------------- Main ----------------
const LandingTerminal = ({ onGetStarted }) => {
  const [quotes, setQuotes] = useState({});
  const [sparks, setSparks] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);
  const [stocksLoaded, setStocksLoaded] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const fetchBatch = async (tickers) => {
      const res = await axios.get(`${API}/stock/batch`, { params: { tickers: tickers.join(',') }, timeout: 20000 });
      const map = {};
      (res.data?.stocks || []).forEach((s) => { if (s && s.ticker) map[s.ticker] = s; });
      return map;
    };
    const chunk = (arr, n) => { const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; };
    // PRIORIDAD: índices/portada + cinta (pocos tickers → carga rápida, ~3-4s)
    const loadPriority = async (retry = true) => {
      try {
        const map = await fetchBatch(TAPE_TICKERS);
        if (!alive) return;
        setQuotes((prev) => ({ ...prev, ...map }));
        setUpdatedAt(new Date());
      } catch (e) {
        if (alive && retry) setTimeout(() => loadPriority(false), 2000);
      }
    };
    // SEGUNDO PLANO: universo de acciones para los movers. Se trocea en lotes
    // pequeños que resuelven de forma INDEPENDIENTE → los datos llegan de forma
    // progresiva y los movers aparecen en cuanto cae el primer lote (antes una
    // sola petición de ~45 tickers se quedaba en "Cargando" con Yahoo saturado).
    const loadChunk = async (group, retry = true) => {
      try {
        const map = await fetchBatch(group);
        if (!alive) return;
        setQuotes((prev) => ({ ...prev, ...map }));
      } catch (e) {
        if (alive && retry) setTimeout(() => loadChunk(group, false), 2500);
      }
    };
    const loadStocks = async () => {
      const groups = chunk(STOCK_UNIVERSE, 10);
      await Promise.all(groups.map((g) => loadChunk(g)));
      if (alive) setStocksLoaded(true);
    };
    const loadSparks = async (retry = true) => {
      try {
        const res = await axios.post(`${API}/market/sparklines`, OVERVIEW.map((o) => o.t), { timeout: 15000 });
        if (!alive) return;
        setSparks(res.data?.sparklines || {});
      } catch (e) {
        if (alive && retry) setTimeout(() => loadSparks(false), 2000);
      }
    };
    loadPriority();
    loadStocks();
    loadSparks();
    timerRef.current = setInterval(() => { loadPriority(false); loadStocks(); }, 60000);
    return () => { alive = false; if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const { gainers, losers } = useMemo(() => {
    const stocks = STOCK_UNIVERSE.map((t) => quotes[t]).filter((q) => q && q.day_change_percent != null);
    const sorted = [...stocks].sort((a, b) => (b.day_change_percent) - (a.day_change_percent));
    return { gainers: sorted.slice(0, 6), losers: [...sorted].reverse().slice(0, 6) };
  }, [quotes]);

  const worldRows = useMemo(() => {
    return ['^GSPC', '^IXIC', '^DJI', '^GDAXI', '^IBEX', '^N225', 'BTC-USD', 'ETH-USD']
      .map((t) => quotes[t]).filter(Boolean)
      .map((q) => ({ ...q, name: TAPE_LABELS[q.ticker] || q.name }));
  }, [quotes]);

  return (
    <div id="mercados" className="relative">
      <style>{`
        @keyframes kap-tape { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-kap-tape { animation: kap-tape 40s linear infinite; }
      `}</style>

      <TopNav onGetStarted={onGetStarted} />
      <TickerTape quotes={quotes} />

      {/* Fondo sutil + resplandor dorado de marca */}
      <div className="relative">
        <div className="absolute inset-0 pointer-events-none opacity-[0.06]" style={{ backgroundImage: 'linear-gradient(rgba(0,220,255,0.5) 1px,transparent 1px),linear-gradient(90deg,rgba(0,220,255,0.5) 1px,transparent 1px)', backgroundSize: '60px 60px' }} />
        {/* Halo dorado detrás del hero (identidad KAPITAL) */}
        <div className="absolute -top-24 left-0 w-[680px] h-[680px] max-w-full pointer-events-none" style={{ background: 'radial-gradient(circle at 30% 30%, rgba(212,175,55,0.20), rgba(212,175,55,0.04) 42%, transparent 68%)' }} />

        <div className="relative max-w-[1600px] mx-auto px-4 md:px-6 pt-4 pb-14">
          {/* Encabezado + buscador */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 lg:gap-10 mb-8">
            <div className="lg:flex-1 max-w-2xl">
              {/* Logotipo de marca protagonista */}
              <img
                src="/logo-kapital-at.png"
                alt="Análisis Técnico Kapital"
                className="h-44 md:h-64 lg:h-80 w-auto object-contain mb-5 select-none"
                style={{ mixBlendMode: 'lighten', filter: 'drop-shadow(0 8px 40px rgba(212,175,55,0.38))' }}
                data-testid="hero-brand-logo"
                draggable="false"
              />
              {/* Franja de marca (dorado + plata) */}
              <div className="flex items-center gap-3 mb-4">
                <span className="text-sm md:text-base font-black uppercase tracking-[0.22em] text-transparent bg-clip-text" style={{ backgroundImage: GOLD }}>Análisis Técnico</span>
                <span className="h-4 w-px bg-gold-300/40" />
                <span className="text-sm md:text-base font-black uppercase tracking-[0.22em] text-transparent bg-clip-text" style={{ backgroundImage: SILVER }}>Kapital</span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00ff88]/10 border border-[#00ff88]/20 mb-4">
                <span className="w-2 h-2 rounded-full bg-[#00ff88] animate-pulse" />
                <span className="text-[11px] font-mono uppercase tracking-wider text-[#00ff88]">Mercados en directo · Datos reales procedentes de Yfinance</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.05]">
                Tu <span className="text-transparent bg-clip-text" style={{ backgroundImage: GOLD }}>Plataforma de Trading</span> profesional
              </h1>
              <p className="mt-3 text-slate-300 text-base md:text-lg max-w-xl">
                Índices, materias primas, cripto y divisas en tiempo real. Análisis técnico e IA, 38 herramientas, 100% en español.
              </p>
              {/* Dominio protagonista para asociación de marca */}
              <button onClick={onGetStarted} className="mt-5 inline-flex items-center gap-2 group kap-link-gold transition-all duration-200" data-testid="brand-domain">
                <span className="text-xl md:text-3xl font-extrabold font-mono tracking-tight text-transparent bg-clip-text" style={{ backgroundImage: GOLD }}>analisistecnicokapital.com</span>
                <ArrowUpRight className="text-gold-300 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-200" size={24} />
              </button>
            </div>
            <div className="w-full lg:w-[38rem] shrink-0">
              <button
                onClick={onGetStarted}
                className="w-full h-14 rounded-xl font-extrabold text-lg text-slate-900 transition-transform duration-200 hover:scale-[1.02] flex items-center justify-center gap-2"
                style={{ background: GOLD, boxShadow: '0 0 32px rgba(212,175,55,0.45)' }}
                data-testid="hero-cta-btn"
              >
                Crear cuenta gratis <ArrowUpRight size={20} />
              </button>
              <p className="mt-2.5 text-center text-sm font-bold">
                <span className="text-transparent bg-clip-text" style={{ backgroundImage: GOLD }}>Sin tarjeta</span>
                <span className="text-slate-400"> · Empieza con </span>
                <span className="text-transparent bg-clip-text" style={{ backgroundImage: GOLD }}>7 herramientas gratis</span>
              </p>

              {/* App descargable — ilustración de móvil (identidad KAPITAL) */}
              <div className="mt-6 relative flex flex-col items-center" data-testid="hero-app-mockup">
                <img
                  src="/kapital-app-phone.png?v=2"
                  alt="Aplicación KAPITAL en el móvil"
                  className="w-full max-w-[30rem] h-auto object-contain select-none rounded-2xl drop-shadow-[0_20px_50px_rgba(0,0,0,0.55)]"
                  draggable="false"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div className="mt-2 text-center">
                  <div className="text-sm md:text-base font-bold text-transparent bg-clip-text" style={{ backgroundImage: GOLD }}>
                    Aplicación descargable desde la web
                  </div>
                  <div className="text-sm md:text-base font-mono font-extrabold text-transparent bg-clip-text" style={{ backgroundImage: SILVER }}>
                    analisistecnicokapital.com
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Franja de confianza — credibilidad de marca */}
          <div className="mb-8 rounded-2xl border border-slate-800 bg-slate-900/40 backdrop-blur-sm px-4 md:px-6 py-5" data-testid="trust-band">
            <p className="mb-5 text-base md:text-lg lg:text-xl font-black leading-tight">
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: GOLD }}>Análisis Técnico Kapital</span>
              <span className="text-white"> es una plataforma de análisis técnico profesional.</span>
            </p>
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
              <div className="flex items-center gap-3 shrink-0">
                <div className="flex -space-x-2">
                  {[0, 1, 2, 3].map((i) => (
                    <span key={i} className="w-7 h-7 rounded-full border border-slate-700" style={{ background: i % 2 ? SILVER : GOLD }} />
                  ))}
                </div>
                <div>
                  <div className="text-xl font-black text-transparent bg-clip-text leading-none" style={{ backgroundImage: GOLD }}>+15.000</div>
                  <div className="text-[11px] text-slate-400 uppercase tracking-wider">traders activos</div>
                </div>
              </div>
              <div className="h-9 w-px bg-slate-800 hidden lg:block" />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3">Brokers sugeridos</div>
                <div className="flex flex-wrap items-center gap-3">
                  {BROKERS.map((b) => (
                    <BrokerLogo key={b.name} name={b.name} cls={b.cls} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Mercados de un vistazo */}
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-transparent bg-clip-text" style={{ backgroundImage: GOLD }} data-testid="market-overview-title">Analizar e invertir, al alcance de todos</h2>
            <div className="flex-1 h-px bg-gradient-to-r from-gold-400/40 to-transparent" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="market-overview-grid">
            {OVERVIEW.map((meta) => (
              <MarketCard key={meta.t} meta={meta} q={quotes[meta.t]} spark={sparks[meta.t]} onOpen={(t) => onGetStarted && onGetStarted(t)} />
            ))}
          </div>

          {/* Tablas densas de movimientos */}
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-transparent bg-clip-text" style={{ backgroundImage: GOLD }} data-testid="movers-title">Movimientos del mercado</h2>
            <div className="flex-1 h-px bg-gradient-to-r from-gold-400/40 to-transparent" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <MoversTable title="Mayores Subidas" rows={gainers} icon={TrendingUp} accent="text-[#00ff88]" testid="movers-gainers" onPick={(t) => onGetStarted && onGetStarted(t)} loading={!stocksLoaded} />
            <MoversTable title="Mayores Bajadas" rows={losers} icon={TrendingDown} accent="text-[#ff3b30]" testid="movers-losers" onPick={(t) => onGetStarted && onGetStarted(t)} loading={!stocksLoaded} />
            <MoversTable title="Índices & Cripto" rows={worldRows} icon={ArrowUpRight} accent="text-gold-400" testid="movers-world" onPick={(t) => onGetStarted && onGetStarted(t)} loading={worldRows.length === 0} />
          </div>

          <div className="mt-4 flex items-center justify-between text-[11px] text-slate-500 font-mono">
            <span>Datos con posible retardo · Solo fines informativos</span>
            {updatedAt && <span data-testid="last-update">Actualizado {updatedAt.toLocaleTimeString('es-ES')}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingTerminal;
